import { CalendarClock, CheckCircle2, FileText, Save, ShieldCheck, Sparkles } from "lucide-react";

const HOW_IT_WORKS = [
  { icon: FileText, text: "Pick a service and answer a few plain-language questions" },
  { icon: CheckCircle2, text: "Check your answers and change anything that isn't right" },
  { icon: ShieldCheck, text: "Add your details, then see your price and download the PDF" },
];

const PROMISES = [
  { icon: CheckCircle2, text: "Plain-language questions" },
  { icon: Save, text: "Save and resume for 30 days" },
  { icon: CalendarClock, text: "Estimate available for 30 days" },
];

/**
 * The top of the calculator page.
 *
 * The wash covers the WHOLE section (inset-0) so the background can never
 * split part-way through the copy — which is what happened on a phone held in
 * landscape, where the hero reflows shorter than a fixed band.
 */
export const CalculatorHero = () => (
  <section data-testid="pricing-calculator-hero" className="relative overflow-hidden">
    <div className="tt-hero-wash tt-hero-fade absolute inset-0" aria-hidden="true" />
    <div className="tt-ruled tt-hero-fade absolute inset-0 opacity-60" aria-hidden="true" />
    <div className="relative mx-auto max-w-6xl px-4 pb-10 pt-12 sm:px-6 sm:pt-16">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
        <div className="lg:col-span-7">
          <span className="tt-stamp">
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            Pricing calculator
          </span>
          <h1 className="mt-5 text-[34px] font-extrabold leading-[1.08] text-foreground sm:text-5xl lg:text-[56px]">
            Know what it costs,
            <br />
            <span className="text-primary">before you pick up the phone.</span>
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            Answer a few plain-language questions about your business and we'll build you an
            indicative quote you can download as a PDF. No jargon and no obligation.
          </p>
          <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2.5 text-sm font-medium text-muted-foreground">
            {PROMISES.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-2">
                <Icon className="h-4 w-4 text-primary" aria-hidden="true" />
                {text}
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-5">
          <div data-testid="pricing-calculator-how-it-works-card" className="tt-card p-6">
            <h2 className="tt-eyebrow">How it works</h2>
            <ol className="mt-4 space-y-4">
              {HOW_IT_WORKS.map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-start gap-3.5">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[color:var(--tt-teal-mist)] text-primary">
                    <Icon className="h-4.5 w-4.5" aria-hidden="true" />
                  </span>
                  <span className="pt-1.5 text-sm leading-relaxed text-foreground">{text}</span>
                </li>
              ))}
            </ol>
            <p className="mt-5 border-t border-border pt-4 text-xs leading-relaxed text-muted-foreground">
              You type in your own figures — there's nothing to upload. Your estimate is based on
              the information you provide and is confirmed once we understand the required scope.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);
